import * as Cesium from 'cesium';
import * as THREE from 'three';
import { TilesRenderer } from '3d-tiles-renderer';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import {
  TREE_CYLINDER_HEIGHT,
  applyTranslationToTreeEntities,
  computeEnuTranslationOffset,
  createTreeEntities
} from './treeCylinders';
import { fitInfiniteCylinder } from './fitCylinderRansac';

const POINT_BOX_HALF_SIZE = 0.5;
const POINT_SAMPLER_CAMERA_DISTANCE = 20;

const createPointCloudSamplerState = () => ({
  initPromise: null,
  tiles: null,
  renderer: null,
  camera: null,
  scene: null,
  raf: 0,
  activePoints: new Map(),
  tileEventHandlers: null,
  loadCount: 0,
  worker: null,
  workerJobId: 0,
  pendingJobs: new Map(),
  disposed: false,
});

const disposePointCloudSampler = (sampler) => {
  if (!sampler) return;
  sampler.disposed = true;
  if (sampler.raf) {
    try {
      cancelAnimationFrame(sampler.raf);
    } catch (e) {
      console.warn('Failed to cancel animation frame:', e);
    }
  }
  if (sampler.tiles && sampler.tileEventHandlers) {
    try {
      sampler.tiles.removeEventListener('load-model', sampler.tileEventHandlers.onLoad);
      sampler.tiles.removeEventListener('dispose-model', sampler.tileEventHandlers.onDispose);
    } catch (e) {
      console.warn('Failed to remove tile event listeners:', e);
    }
  }
  if (sampler.tiles && sampler.tiles.dispose) {
    try {
      sampler.tiles.dispose();
    } catch (e) {
      console.warn('Failed to dispose tiles renderer:', e);
    }
  }
  if (sampler.renderer) {
    try {
      sampler.renderer.dispose();
      sampler.renderer.forceContextLoss?.();
    } catch (e) {
      console.warn('Failed to dispose WebGL renderer:', e);
    }
  }
  if (sampler.worker) {
    try {
      sampler.worker.terminate();
    } catch (e) {
      console.warn('Failed to terminate worker:', e);
    }
  }
  sampler.activePoints.clear();
  sampler.pendingJobs.clear();
  sampler.tiles = null;
  sampler.renderer = null;
  sampler.camera = null;
  sampler.scene = null;
  sampler.tileEventHandlers = null;
  sampler.raf = 0;
  sampler.loadCount = 0;
  sampler.worker = null;
  sampler.workerJobId = 0;
  sampler.initPromise = null;
};

const configureDracoLoader = (tilesRenderer) => {
  try {
    const dracoLoader = new DRACOLoader(tilesRenderer.manager);
    const DRACO_CDN = 'https://www.gstatic.com/draco/versioned/decoders/1.5.7/';
    const DRACO_LOCAL = '/libs/draco/';
    const useLocalDraco = false;

    dracoLoader.setDecoderPath(useLocalDraco ? DRACO_LOCAL : DRACO_CDN);
    dracoLoader.setDecoderConfig({ type: 'wasm' });
    dracoLoader.preload();
    tilesRenderer.manager.addHandler(/\.drc$/i, dracoLoader);
  } catch (error) {
    console.warn('Failed to configure DRACO loader.', error);
  }
};

const makeIonTilesRenderer = async (assetId, ionAccessToken, threeCamera, threeRenderer) => {
  const endpointUrl = new URL(`https://api.cesium.com/v1/assets/${assetId}/endpoint`);
  endpointUrl.searchParams.append('access_token', ionAccessToken);

  const endpointResponse = await fetch(endpointUrl, { mode: 'cors' });
  const endpointData = await endpointResponse.json();
  const tilesetUrl = new URL(endpointData.url);
  const tilesetVersion = tilesetUrl.searchParams.get('v');

  const tilesRenderer = new TilesRenderer(tilesetUrl);
  tilesRenderer.fetchOptions = { headers: { Authorization: `Bearer ${endpointData.accessToken}` } };
  tilesRenderer.preprocessURL = (uri) => {
    const processedUrl = new URL(uri);
    processedUrl.searchParams.append('v', tilesetVersion);
    return processedUrl.toString();
  };

  tilesRenderer.setCamera(threeCamera);
  tilesRenderer.setResolutionFromRenderer(threeCamera, threeRenderer);
  tilesRenderer.displayActiveTiles = true;
  configureDracoLoader(tilesRenderer);

  return tilesRenderer;
};

const ensurePointCloudSampler = async ({ sampler, pointCloudTilesetId, token }) => {
  if (!pointCloudTilesetId || !token) {
    console.warn('Point cloud asset ID or token is missing.');
    return null;
  }

  if (!sampler) return null;
  if (sampler.initPromise) {
    return sampler.initPromise;
  }

  sampler.initPromise = (async () => {
    sampler.disposed = false;
    const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: 'high-performance' });
    renderer.setSize(600, 600, false);
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1e8);
    const scene = new THREE.Scene();

    sampler.renderer = renderer;
    sampler.camera = camera;
    sampler.scene = scene;

    const tiles = await makeIonTilesRenderer(pointCloudTilesetId, token, camera, renderer);
    sampler.tiles = tiles;
    tiles.errorTarget = 2;
    scene.add(tiles.group);

    const onLoad = ({ scene: tileScene }) => {
      tileScene.traverse((obj) => {
        if (obj.isPoints) {
          sampler.activePoints.set(obj.uuid, obj);
        }
      });
      sampler.loadCount += 1;
    };
    const onDispose = ({ scene: tileScene }) => {
      tileScene.traverse((obj) => {
        if (obj.isPoints) {
          sampler.activePoints.delete(obj.uuid);
        }
      });
    };
    sampler.tileEventHandlers = { onLoad, onDispose };
    tiles.addEventListener('load-model', onLoad);
    tiles.addEventListener('dispose-model', onDispose);

    const loop = () => {
      if (sampler.disposed) return;
      tiles.update();
      sampler.raf = requestAnimationFrame(loop);
    };
    loop();

    return sampler;
  })();
  sampler.initPromise.catch(() => {
    sampler.initPromise = null;
  });

  return sampler.initPromise;
};

const focusSamplerCamera = (sampler, center, cameraDistance = POINT_SAMPLER_CAMERA_DISTANCE) => {
  if (!sampler?.tiles || !sampler.camera) return;
  const tilesGroup = sampler.tiles.group;
  tilesGroup.updateMatrixWorld(true);

  const upVector = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(center, new Cesium.Cartesian3());
  const cameraPosition = Cesium.Cartesian3.add(
    center,
    Cesium.Cartesian3.multiplyByScalar(upVector, cameraDistance, new Cesium.Cartesian3()),
    new Cesium.Cartesian3()
  );

  const cesiumToThree = (cesiumVector) => new THREE.Vector3(cesiumVector.x, cesiumVector.y, cesiumVector.z);
  const cameraPositionThree = cesiumToThree(cameraPosition).applyMatrix4(tilesGroup.matrixWorld);
  const lookAtPositionThree = cesiumToThree(center).applyMatrix4(tilesGroup.matrixWorld);
  const rotationMatrix = new THREE.Matrix3().setFromMatrix4(tilesGroup.matrixWorld);
  const upVectorThree = cesiumToThree(upVector).applyMatrix3(rotationMatrix).normalize();

  sampler.camera.position.copy(cameraPositionThree);
  sampler.camera.up.copy(upVectorThree);
  sampler.camera.lookAt(lookAtPositionThree);
  sampler.camera.updateProjectionMatrix();
};

const waitForSamplerLoad = (sampler, timeoutMs = 800) => new Promise((resolve) => {
  const start = performance.now();
  const initialCount = sampler.loadCount;
  const tick = () => {
    if (sampler.disposed) {
      resolve(false);
      return;
    }
    if (sampler.loadCount > initialCount) {
      resolve(true);
      return;
    }
    if (performance.now() - start >= timeoutMs) {
      resolve(false);
      return;
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
});

const buildPointCloudItems = (sampler) => {
  const tilesGroup = sampler.tiles?.group;
  if (!tilesGroup) return { items: [], transferableBuffers: [] };

  tilesGroup.updateMatrixWorld(true);
  const tilesGroupInverse = new THREE.Matrix4().copy(tilesGroup.matrixWorld).invert();
  const items = [];
  const transferableBuffers = [];

  sampler.activePoints.forEach((pointObject) => {
    if (!pointObject?.isPoints || !pointObject.geometry) return;
    const positionAttribute = pointObject.geometry.getAttribute('position');
    if (!positionAttribute?.array) return;

    pointObject.updateMatrixWorld(true);
    const sourcePositions = positionAttribute.array;
    const positionsCopy = new Float32Array(sourcePositions.length);
    positionsCopy.set(sourcePositions);
    transferableBuffers.push(positionsCopy.buffer);

    const combinedMatrix = new THREE.Matrix4();
    combinedMatrix.copy(tilesGroupInverse).multiply(pointObject.matrixWorld);
    const transformMatrix = new Float64Array(combinedMatrix.elements);

    items.push({ positions: positionsCopy, matrix: transformMatrix });
  });

  return { items, transferableBuffers };
};

const runPointCloudBoxWorker = (sampler, center, items, transferableBuffers, halfSize = POINT_BOX_HALF_SIZE) => {
  if (!sampler.worker) {
    sampler.worker = new Worker(new URL('./pointCloudBox.worker.js', import.meta.url));
    sampler.worker.onmessage = (event) => {
      const data = event.data;
      if (!data || data.type !== 'result') return;
      const resolver = sampler.pendingJobs.get(data.jobId);
      if (!resolver) return;
      sampler.pendingJobs.delete(data.jobId);
      resolver({ points: data.points, count: data.count });
    };
  }

  const jobId = ++sampler.workerJobId;
  const centerArray = [center.x, center.y, center.z];

  return new Promise((resolve) => {
    sampler.pendingJobs.set(jobId, resolve);
    sampler.worker.postMessage(
      {
        type: 'filter',
        jobId,
        center: centerArray,
        halfSize: Number(halfSize) || 0,
        items,
      },
      transferableBuffers || []
    );
  });
};

const samplePointCloudBox = async ({
  sampler,
  center,
  pointCloudTilesetId,
  token,
  halfSize = POINT_BOX_HALF_SIZE,
  cameraDistance = POINT_SAMPLER_CAMERA_DISTANCE,
  timeoutMs = 800
}) => {
  const activeSampler = await ensurePointCloudSampler({ sampler, pointCloudTilesetId, token });
  if (!activeSampler || !activeSampler.tiles) {
    return null;
  }

  focusSamplerCamera(activeSampler, center, cameraDistance);
  const loaded = await waitForSamplerLoad(activeSampler, timeoutMs);
  if (!loaded) {
    console.warn('Point cloud tiles may still be loading; sampling may be incomplete.');
  }

  const { items, transferableBuffers } = buildPointCloudItems(activeSampler);
  if (!items.length) {
    console.warn('No point cloud tiles loaded for sampling.');
    return null;
  }

  return runPointCloudBoxWorker(activeSampler, center, items, transferableBuffers, halfSize);
};

const addFittedCylinderFromPoints = ({
  viewer,
  tileset,
  assetId,
  pointArray,
  geo,
  pointGeo,
  pointValues,
  resolveTreeColor,
  setLoadedGeoJsons
}) => {
  if (!viewer || !Array.isArray(pointArray) || pointArray.length < 10) {
    console.warn('Not enough points to fit a cylinder.');
    return;
  }

  if (!geo) {
    console.warn('GeoJSON asset is not loaded yet.');
    return;
  }

  let fitResult;
  try {
    fitResult = fitInfiniteCylinder(pointArray);
  } catch (error) {
    console.warn('Cylinder fit failed:', error);
    return;
  }

  const axisPoint = new Cesium.Cartesian3(
    fitResult.axisPoint[0],
    fitResult.axisPoint[1],
    fitResult.axisPoint[2]
  );
  const axisDir = new Cesium.Cartesian3(
    fitResult.axisDir[0],
    fitResult.axisDir[1],
    fitResult.axisDir[2]
  );
  Cesium.Cartesian3.normalize(axisDir, axisDir);

  const centerPosition = axisPoint;

  // The picked point sits in the tileset's transformed frame. Subtract the
  // current ENU translation so the tree's stored coordinates are in the
  // untransformed (base) frame — the load/refresh effect will re-apply the
  // offset, keeping the cylinder glued to the spot the user clicked and
  // moving with future transform adjustments.
  const offset = computeEnuTranslationOffset(pointGeo, pointValues);
  const basePosition = offset
    ? Cesium.Cartesian3.subtract(centerPosition, offset, new Cesium.Cartesian3())
    : centerPosition;

  const enu = Cesium.Transforms.eastNorthUpToFixedFrame(basePosition);
  const east = Cesium.Matrix4.getColumn(enu, 0, new Cesium.Cartesian3());
  const north = Cesium.Matrix4.getColumn(enu, 1, new Cesium.Cartesian3());
  const up = Cesium.Matrix4.getColumn(enu, 2, new Cesium.Cartesian3());
  const localAxis = new Cesium.Cartesian3(
    Cesium.Cartesian3.dot(axisDir, east),
    Cesium.Cartesian3.dot(axisDir, north),
    Cesium.Cartesian3.dot(axisDir, up)
  );
  Cesium.Cartesian3.normalize(localAxis, localAxis);

  const carto = Cesium.Cartographic.fromCartesian(basePosition);
  const lon = Cesium.Math.toDegrees(carto.longitude);
  const lat = Cesium.Math.toDegrees(carto.latitude);
  const elevation = carto.height;

  let centerX = 0;
  let centerY = 0;
  const origin = tileset?.clippingPlanesOriginMatrix || tileset?.root?.transform;
  if (origin) {
    const inverse = Cesium.Matrix4.inverse(origin, new Cesium.Matrix4());
    const localPosition = Cesium.Matrix4.multiplyByPoint(inverse, basePosition, new Cesium.Cartesian3());
    if (Number.isFinite(localPosition.x)) centerX = localPosition.x;
    if (Number.isFinite(localPosition.y)) centerY = localPosition.y;
  }

  const existingIds = (geo.trees || [])
    .map(tree => Number(tree.id))
    .filter(id => Number.isFinite(id));
  const nextId = existingIds.length ? Math.max(...existingIds) + 1 : `tree_${Date.now()}`;

  const newTree = {
    id: nextId,
    lon,
    lat,
    elevation,
    x: centerX,
    y: centerY,
    radius: fitResult.radius,
    direction: localAxis,
    treeHeight: 'N/A',
    properties: {
      id: nextId,
      radius: fitResult.radius,
      height: 'N/A',
      dx: localAxis.x,
      dy: localAxis.y,
      dz: localAxis.z,
      center_lon: lon,
      center_lat: lat,
      center_z: elevation,
    }
  };

  const newEntities = createTreeEntities(viewer, [newTree], {
    color: resolveTreeColor(assetId),
    cylinderHeight: TREE_CYLINDER_HEIGHT,
    heightOffset: 0
  });

  if (!newEntities.length) {
    console.warn('Failed to create a tree entity from the fitted cylinder.');
    return;
  }

  // Re-apply the current ENU translation so the cylinder lands at the picked
  // (transformed) position and tracks future transform changes.
  applyTranslationToTreeEntities(newEntities, pointGeo, pointValues);

  setLoadedGeoJsons(prev => {
    const current = prev[assetId];
    if (!current) return prev;
    return {
      ...prev,
      [assetId]: {
        ...current,
        trees: [...(current.trees || []), newTree],
        entities: [...(current.entities || []), ...newEntities],
      }
    };
  });

  console.log('Added tree from fitted cylinder:', {
    id: nextId,
    radius: fitResult.radius,
    height: newTree.properties.height,
    center_x: centerX,
    center_y: centerY,
    axisPoint: fitResult.axisPoint,
    axisDir: fitResult.axisDir,
    inliers: fitResult.inliers?.length
  });
};

export {
  createPointCloudSamplerState,
  disposePointCloudSampler,
  ensurePointCloudSampler,
  samplePointCloudBox,
  addFittedCylinderFromPoints
};
